/**
 * Signing in, for both the learner app and the facilitator dashboard.
 *
 * One screen and one gate, because there is one set of accounts. A learner and a
 * facilitator type into the same three boxes; what differs is what they see afterwards.
 *
 * The form is written for a ten-year-old on a shared lab machine. That drives most of
 * the decisions here: the fields say what they mean in plain words, nothing is
 * remembered between people, the failure message never blames a particular box (the
 * server does not say which was wrong, and a UI that guessed would be worse than
 * silent), and there is no "stay signed in".
 *
 * **Two gates, not one, and only the first is per-device.** A device that has never been
 * here answers the enrolment phrase once and then never sees it again; every visit after
 * that goes straight to the sign-in form. That is the whole point of the shape — the
 * shared password it replaced interrupted every single visit, and sometimes twice in one
 * sign-in. See D39.
 */

import { useCallback, useEffect, useState } from "react";

import { Aurora } from "./Aurora";
import { ApiError, api, type Me } from "./api";

export type AuthState =
  | { status: "checking" }
  | { status: "out" }
  | { status: "enrol" }
  | { status: "in"; me: Me };

/**
 * Ask the server who we are, once, on load.
 *
 * A 401 is the normal answer for a signed-out browser, not an error worth showing —
 * every session eventually expires, and arriving at a login screen is the correct
 * outcome rather than something to apologise for.
 */
export function useAuth(): {
  state: AuthState;
  signedIn: (me: Me) => void;
  enrolled: () => void;
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({ status: "checking" });

  useEffect(() => {
    let live = true;
    void api
      .me()
      .then((me) => live && setState({ status: "in", me }))
      .catch(async () => {
        if (!live) return;
        // Signed out is the ordinary answer. Before showing the sign-in form, ask whether
        // this device may use it at all — otherwise an unenrolled device is offered a form
        // whose every submission is refused, which reads as "my phrase is wrong".
        try {
          const gate = await api.enrolmentState();
          if (live) setState({ status: gate.required && !gate.enrolled ? "enrol" : "out" });
        } catch {
          if (live) setState({ status: "out" });
        }
      });
    return () => {
      live = false;
    };
  }, []);

  const signedIn = useCallback((me: Me) => setState({ status: "in", me }), []);
  const enrolled = useCallback(() => setState({ status: "out" }), []);

  const signOut = useCallback(async () => {
    // Clear locally even if the call fails: a browser that cannot reach the server must
    // still be able to get someone out of their account on a shared machine.
    try {
      await api.logout();
    } finally {
      setState({ status: "out" });
    }
  }, []);

  return { state, signedIn, enrolled, signOut };
}

/**
 * The device gate: one phrase, once, then never again on this device.
 *
 * Deliberately says almost nothing. It is answered before anybody has signed in, so it
 * cannot hint at the phrase, and it must not imply that the person in front of it has done
 * something wrong — most people meeting this screen are on a new laptop, not intruding.
 */
export function Enrol({ onEnrolled }: { onEnrolled: () => void }) {
  const [phrase, setPhrase] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.enrol(phrase);
      onEnrolled();
    } catch (error) {
      setProblem(
        error instanceof ApiError
          ? error.message
          : "Could not reach the tutor. Tell a facilitator.",
      );
      setPhrase("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* A sibling of <main>, never a child of it.
          `.setup.lit` uses `backdrop-filter`, and that makes an element a containing
          block for `position: fixed` descendants — so nesting this inside the card
          clipped the whole background to the card, which is exactly backwards. */}
      <Aurora />
      <main className="setup login lit">
      <h1>QRL Tutor</h1>
      <p className="lead">First time on this device? Enter the QRL phrase to get started.</p>

      {problem && (
        <div className="problem" role="alert">
          <p>{problem}</p>
        </div>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          The QRL phrase
          <input
            value={phrase}
            onChange={(event) => setPhrase(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
        </label>
        <button type="submit" className="primary" disabled={busy || !phrase.trim()}>
          {busy ? "Checking…" : "Continue"}
        </button>
      </form>

      <p className="muted">
        You only do this once on each device. Ask a facilitator for the phrase — it is the
        same one for everybody, and it is not your own sign-in.
      </p>
    </main>
    </>
  );
}

export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [nickname, setNickname] = useState("");
  const [membership, setMembership] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      onSignedIn(await api.login(nickname, membership, passphrase));
    } catch (error) {
      // The server's message is deliberately the same whichever factor was wrong, and
      // it is written to be read by a learner, so it is shown as-is.
      setProblem(
        error instanceof ApiError ? error.message : "Could not reach the tutor. Tell a facilitator.",
      );
      setPassphrase("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* A sibling of <main>, never a child of it.
          `.setup.lit` uses `backdrop-filter`, and that makes an element a containing
          block for `position: fixed` descendants — so nesting this inside the card
          clipped the whole background to the card, which is exactly backwards. */}
      <Aurora />
      <main className="setup login lit">
      <h1>QRL Tutor</h1>
      <p className="lead">Sign in to carry on where you left off.</p>

      {problem && (
        <div className="problem" role="alert">
          <p>{problem}</p>
        </div>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          Your nickname
          <input
            value={nickname}
            onChange={(event) => setNickname(event.target.value)}
            autoComplete="username"
            spellCheck={false}
            autoFocus
          />
        </label>
        <label>
          Your membership number
          <input
            value={membership}
            onChange={(event) => setMembership(event.target.value)}
            // `inputMode=numeric` so a tablet offers a number pad. Still not
            // `type=number`: a membership number is a label, not a quantity, and the
            // spinner and scroll-to-change that come with `type=number` are wrong for it.
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder="114"
          />
        </label>
        <label>
          Your three words
          <input
            // `type=password` so it cannot be read over a shoulder. No `inputMode`: this
            // is words now, so the ordinary keyboard is the right one.
            type="password"
            value={passphrase}
            onChange={(event) => setPassphrase(event.target.value)}
            autoComplete="current-password"
          />
          <span className="hint">Capitals and extra spaces do not matter.</span>
        </label>
        <button
          type="submit"
          className="primary"
          disabled={busy || !nickname.trim() || !membership.trim() || !passphrase.trim()}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="muted">
        Forgotten your words? A facilitator can sort it out — nobody can look them up, so
        they will help you pick new ones.
      </p>
    </main>
    </>
  );
}

/** Shown while `/api/auth/me` is in flight, so the login form does not flash into view. */
export function Checking() {
  return (
    <>
      {/* A sibling of <main>, never a child of it.
          `.setup.lit` uses `backdrop-filter`, and that makes an element a containing
          block for `position: fixed` descendants — so nesting this inside the card
          clipped the whole background to the card, which is exactly backwards. */}
      <Aurora />
      <main className="setup lit">
      <h1>QRL Tutor</h1>
      <p className="muted">One moment…</p>
    </main>
    </>
  );
}

/**
 * The signed-in strip: who you are, and the way out.
 *
 * "Sign out" matters more here than in most apps. These are shared lab machines, and
 * the next person to sit down must not inherit the last one's account.
 */
export function WhoAmI({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  return (
    <span className="whoami">
      <span className="who">{me.nickname}</span>
      {me.role !== "learner" && <span className="role">{me.role.replace("_", " ")}</span>}
      <button type="button" className="ghost" onClick={onSignOut}>
        Sign out
      </button>
    </span>
  );
}
